import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import JobInputActions from "./shared/JobInputActions";
import useJobsPagination from "./shared/useJobsPagination";


const DEFAULT_SHEET = "Product MAP Status";

const STATUS_SCHEDULED = "scheduled";
const STATUS_DONE = "done";
const STATUS_FAILED = "failed";
const STATUS_CANCELLED = "cancelled";

const RUN_NOW = "now";
const RUN_SCHEDULE = "schedule";

const formatLocalDatetimeInput = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const formatScheduledDisplay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

// identifier_column values accepted by the API. Empty string = auto.
const ID_COL_AUTO = "";
const ID_COL_SKU = "sku";
const ID_COL_TITLE = "title";

const ID_COL_OPTIONS = [
  { value: ID_COL_AUTO, label: "Auto (SKU → Title)" },
  { value: ID_COL_SKU, label: "SKU column" },
  { value: ID_COL_TITLE, label: "Title column" },
];

const ID_COL_HELPER = {
  [ID_COL_AUTO]:
    "Tries SKU first when the row has one, falls back to Title otherwise. Use when your file has both columns.",
  [ID_COL_SKU]:
    "Uses only the SKU column (accepts headers 'SKU', 'Variant SKU', or 'Parent SKU'). Matches the parent SKU on Shopify.",
  [ID_COL_TITLE]:
    "Uses only the Title column (accepts 'Title', 'Product Title', or 'Input Product'). Case-insensitive lookup.",
};

function MapStatusReconcileTool() {
  const [inputFile, setInputFile] = useState(null);
  const [sheetName, setSheetName] = useState(DEFAULT_SHEET);
  const [identifierColumn, setIdentifierColumn] = useState(ID_COL_AUTO);
  const [dryRun, setDryRun] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [runTime, setRunTime] = useState(RUN_NOW);
  const [scheduledAt, setScheduledAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [downloadingInputJobId, setDownloadingInputJobId] = useState(null);
  const [restartingJobId, setRestartingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const minScheduledAt = formatLocalDatetimeInput(new Date());

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: "/shopify/reconcile-map-status/jobs",
    cacheKey: "mapStatusReconcile.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "Reconcile MAP Status",
    description:
      "For each row of a 'Product MAP Status' tab, find the latest onmap_/offmap_<MM.DD.YY> tag on the matched Shopify product, remove the obsolete map tags, and write the new status (ON MAP / OFF MAP / NO MAP TAG) back into the sheet.",
    steps: [
      "Upload your MAP-status XLSX. The chosen tab must contain a SKU column (accepts 'SKU', 'Variant SKU', or 'Parent SKU') and/or a Title column ('Title', 'Product Title', or 'Input Product'). 'Current MAP Status' is written by the tool.",
      "Pick the identifier column. Auto cascades SKU → Title; pick a specific column to disable the fallback. SKU is the more reliable match — use it when your file has parent SKUs.",
      "Override the tab name only if your workbook uses a different one. Default is 'Product MAP Status'.",
      "Optional: Dry Run — reports what would be removed without calling Shopify's tagsRemove. Use to preview before mutating.",
      "Click Start. You'll get a job ID and live progress.",
      "Leave the page if you like — jobs run server-side and reappear here when you return.",
      "When done, download the reconciled workbook. Every input tab is preserved; the chosen tab has the Current MAP Status column updated. Two new tabs are added: 'Reconcile Summary' and 'Tag Changes' (per-row before/after, with the column that resolved the match).",
    ],
    templates: [],
  };

  const guideTips = [
    "Tag format expected: onmap_MM.DD.YY or offmap_MM.DD.YY (e.g. 'onmap_05.27.26'). This is what the SKU builder writes.",
    "'Latest' is decided by parsing the MM.DD.YY date. Same-date ties (one on, one off) prefer the later tag in Shopify's stored tag order.",
    "Identifier priority in Auto mode: SKU column first when populated, Title as fallback. Pick a specific column to disable cascade — useful when one column is dirty.",
    "Products that match no on/off-map tag are reported as 'NO MAP TAG' — no removals happen for them.",
    "Products with no Shopify match are reported as 'NOT FOUND'. The Current MAP Status cell is overwritten to 'NOT FOUND' so the row stands out in the report.",
    "Dry Run mode still does the lookups (so it's not instant) but skips every tagsRemove call. Helpful as a preview pass on a large file.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prevStatus = previous[j.job_id];
      if (prevStatus && prevStatus !== j.status) {
        if (j.status === STATUS_DONE) {
          addToast(`Reconcile ${j.job_id} complete. Report is ready.`, "success");
        } else if (j.status === STATUS_FAILED) {
          addToast(
            `Reconcile ${j.job_id} failed: ${j.error || "unknown error"}`,
            "error"
          );
        } else if (j.status === STATUS_CANCELLED) {
          addToast(`Reconcile ${j.job_id} cancelled.`, "info");
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(
      jobs.map((j) => [j.job_id, j.status])
    );
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!inputFile) {
      addToast("Please upload a workbook.", "error");
      return;
    }
    const maxSize = 100 * 1024 * 1024;
    if (inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    let scheduledIso = null;
    if (runTime === RUN_SCHEDULE) {
      if (!scheduledAt) {
        addToast("Pick a date and time to schedule the job.", "error");
        return;
      }
      const target = new Date(scheduledAt);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast("Scheduled date/time must be at least 30 seconds in the future.", "error");
        return;
      }
      scheduledIso = target.toISOString();
    }

    const formData = new FormData();
    formData.append("input_file", inputFile);
    const trimmedSheet = sheetName.trim();
    if (trimmedSheet) {
      formData.append("sheet_name", trimmedSheet);
    }
    if (identifierColumn) {
      formData.append("identifier_column", identifierColumn);
    }
    if (dryRun) {
      formData.append("dry_run", "true");
    }
    formData.append("include_archived", includeArchived ? "true" : "false");
    if (scheduledIso) {
      formData.append("scheduled_at", scheduledIso);
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await flaskApi.post("/shopify/reconcile-map-status", formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          setUploadProgress(percentCompleted);
        },
      });

      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      if (scheduledIso) {
        addToast(
          `Reconcile ${jobId} scheduled for ${formatScheduledDisplay(scheduledIso)}.`,
          "info"
        );
      } else {
        addToast(`Reconcile ${jobId} queued. Tracking progress…`, "info");
      }
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setInputFile(null);
      setScheduledAt("");
      setRunTime(RUN_NOW);
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to start reconcile job.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = async (job) => {
    if (!job?.job_id) return;
    const ok = window.confirm(
      `Cancel scheduled reconcile ${job.job_id}? This can't be undone.`
    );
    if (!ok) return;
    setCancellingJobId(job.job_id);
    try {
      await flaskApi.delete(`/shopify/reconcile-map-status/jobs/${job.job_id}`);
      addToast(`Reconcile ${job.job_id} cancelled.`, "success");
      await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to cancel reconcile.");
      addToast(message, "error");
    } finally {
      setCancellingJobId(null);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/shopify/reconcile-map-status/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "map_status_reconciled.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addToast("Report downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to download report.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  const handleDownloadInput = async (job) => {
    if (!job?.job_id) return;
    setDownloadingInputJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/shopify/reconcile-map-status/jobs/${job.job_id}/input`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute("download", extractedFilename || job.input_filename || "input_file");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Input file downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to download input file.");
      addToast(message, "error");
    } finally {
      setDownloadingInputJobId(null);
    }
  };

  const handleRestart = async (job, scheduledAtLocal) => {
    if (!job?.job_id) return;
    let scheduledIso = null;
    if (scheduledAtLocal) {
      const target = new Date(scheduledAtLocal);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast("Scheduled date/time must be at least 30 seconds in the future.", "error");
        return;
      }
      scheduledIso = target.toISOString();
    }
    setRestartingJobId(job.job_id);
    try {
      await flaskApi.post(
        `/shopify/reconcile-map-status/jobs/${job.job_id}/restart`,
        scheduledIso ? { scheduled_at: scheduledIso } : {}
      );
      addToast(
        scheduledIso
          ? `Reconcile ${job.job_id} rescheduled for ${formatScheduledDisplay(scheduledIso)}.`
          : `Reconcile ${job.job_id} restarted.`,
        scheduledIso ? "info" : "success"
      );
      if (page !== 1) await setPage(1);
      else await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to restart job.");
      addToast(message, "error");
    } finally {
      setRestartingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Reconcile MAP Status"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Input Workbook</h3>
              </div>
              <ToolFileUploadField
                label="MAP status workbook"
                file={inputFile}
                setFile={setInputFile}
                accept=".xlsx,.xls"
                helperText="XLSX with a SKU column (SKU / Variant SKU / Parent SKU) and/or a Title column (Title / Product Title / Input Product). 'Current MAP Status' is written by the tool."
              />
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Tab Name</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">Sheet/tab to reconcile</label>
                <p className="tool-helper-text">
                  Default is <code>Product MAP Status</code>. Case-sensitive — must match exactly. Other tabs in the workbook are passed through untouched.
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder="Product MAP Status"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  disabled={uploading}
                />
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Identifier Column</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label" htmlFor="reconcile-identifier-column">
                  Which column to use for Shopify lookup
                </label>
                <select
                  id="reconcile-identifier-column"
                  className="tool-input"
                  value={identifierColumn}
                  onChange={(event) => setIdentifierColumn(event.target.value)}
                  disabled={uploading}
                >
                  {ID_COL_OPTIONS.map((opt) => (
                    <option key={opt.value || "auto"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="tool-helper-text">
                  {ID_COL_HELPER[identifierColumn]}
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Mode</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-checkbox-option">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(event) => setDryRun(event.target.checked)}
                    disabled={uploading}
                  />
                  <span>Dry Run — report only, no tagsRemove on Shopify</span>
                </label>
                <span className="tool-helper-text">
                  Dry Run still runs every product lookup, but skips Shopify mutations. Useful for a preview pass on a large file before committing.
                </span>
                <label className="tool-checkbox-option">
                  <input
                    type="checkbox"
                    checked={includeArchived}
                    onChange={(event) => setIncludeArchived(event.target.checked)}
                    disabled={uploading}
                  />
                  <span>Also reconcile archived products</span>
                </label>
                <span className="tool-helper-text">
                  By default only active products are matched. Tick this to also reconcile archived products (obsolete map tags removed, status written) — matched archived products are flagged in the report. Draft products are always skipped.
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">When to Run</h3>
              </div>
              <div className="tool-field-stack">
                <div
                  className="tool-radio-group"
                  role="radiogroup"
                  aria-label="When to run"
                >
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="reconcile-run-time"
                      value={RUN_NOW}
                      checked={runTime === RUN_NOW}
                      onChange={() => setRunTime(RUN_NOW)}
                      disabled={uploading}
                    />
                    <span>Run now</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="reconcile-run-time"
                      value={RUN_SCHEDULE}
                      checked={runTime === RUN_SCHEDULE}
                      onChange={() => setRunTime(RUN_SCHEDULE)}
                      disabled={uploading}
                    />
                    <span>Schedule for later</span>
                  </label>
                </div>
                {runTime === RUN_SCHEDULE ? (
                  <>
                    <label className="tool-label" htmlFor="reconcile-scheduled-at">
                      Date &amp; time
                    </label>
                    <input
                      id="reconcile-scheduled-at"
                      type="datetime-local"
                      className="tool-input"
                      value={scheduledAt}
                      min={minScheduledAt}
                      onChange={(event) => setScheduledAt(event.target.value)}
                      disabled={uploading}
                      required
                    />
                    <span className="tool-helper-text">
                      Uses your browser's local time. The file is uploaded now and the reconcile will fire at the scheduled time — you can close the page in between.
                    </span>
                  </>
                ) : null}
              </div>
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={uploading}>
                {uploading
                  ? "Uploading…"
                  : runTime === RUN_SCHEDULE
                  ? "Schedule Reconcile"
                  : "Start Reconcile"}
              </button>

              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading file…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Reconcile Jobs"
          emptyText="No reconcile jobs yet. Submit a file above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
                const progress = j.progress || {};
                const total = Number(progress.total) || 0;
                const processed = Number(progress.processed) || 0;
                const pct =
                  total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
                return (
                  <div key={j.job_id} className="tool-job-status">
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Job ID</span>
                      <span className="tool-job-value">{j.job_id}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Input</span>
                      <span className="tool-job-value">{j.input_filename || "—"}</span>
                    </div>
                    {j.sheet_name ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Tab</span>
                        <span className="tool-job-value">{j.sheet_name}</span>
                      </div>
                    ) : null}
                    {j.include_archived ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Archived</span>
                        <span className="tool-job-value">Included</span>
                      </div>
                    ) : null}
                    {j.scheduled_at ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Scheduled</span>
                        <span className="tool-job-value">
                          {formatScheduledDisplay(j.scheduled_at)}
                        </span>
                      </div>
                    ) : null}
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Status</span>
                      <span className={`tool-job-value tool-job-status-${j.status}`}>
                        {j.status}
                      </span>
                    </div>
                    {total > 0 ? (
                      <>
                        <div className="tool-job-status-row">
                          <span className="tool-job-label">Progress</span>
                          <span className="tool-job-value">
                            {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                          </span>
                        </div>
                        <div className="progress-bar-container">
                          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                      </>
                    ) : null}
                    <div className="tool-job-counts">
                      <span>ON MAP: {progress.on_map || 0}</span>
                      <span>OFF MAP: {progress.off_map || 0}</span>
                      <span>No tag: {progress.no_tag || 0}</span>
                      <span>Not found: {progress.not_found || 0}</span>
                      <span>Tags removed: {progress.tags_removed || 0}</span>
                      <span>Errors: {progress.errors || 0}</span>
                    </div>
                    {j.status === STATUS_FAILED && j.error ? (
                      <div className="tool-job-error">Error: {j.error}</div>
                    ) : null}
                    {j.status === STATUS_DONE ? (
                      <button
                        type="button"
                        className="tool-btn-primary"
                        onClick={() => handleDownload(j)}
                        disabled={downloadingJobId === j.job_id}
                      >
                        {downloadingJobId === j.job_id ? "Downloading…" : "Download Report"}
                      </button>
                    ) : null}
                    {j.status === STATUS_SCHEDULED ? (
                      <button
                        type="button"
                        className="tool-btn-secondary"
                        onClick={() => handleCancel(j)}
                        disabled={cancellingJobId === j.job_id}
                      >
                        {cancellingJobId === j.job_id ? "Cancelling…" : "Cancel"}
                      </button>
                    ) : null}
                    <JobInputActions
                      job={j}
                      onDownloadInput={handleDownloadInput}
                      downloadingInput={downloadingInputJobId === j.job_id}
                      onRestart={handleRestart}
                      restarting={restartingJobId === j.job_id}
                      minScheduledAt={minScheduledAt}
                    />
                  </div>
                );
              }}
            />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default MapStatusReconcileTool;
